import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./DeviceList.css";

export default function DeviceList() {
  const [devices, setDevices] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({});
  const [loading, setLoading] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState(null);

  const menuRef = useRef(null);
  const navigate = useNavigate();

  const API_BASE = process.env.REACT_APP_API_BASE_URL;
  const base = (API_BASE || "").replace(/\/+$/, "");

  const fetchDevices = async (page = 1, silent = false) => {
    try {
      if (!silent) setLoading(true);

      const token = localStorage.getItem("token");

      const res = await axios.get(`${base}/api/getAllDevices?page=${page}`, {
        headers: token
          ? {
              Authorization: `Bearer ${token}`,
            }
          : {},
      });

      const responseData = res.data?.data;

      const sortedDevices = (responseData?.data || []).sort((a, b) =>
        (a.name || "").localeCompare(b.name || ""),
      );

      setDevices(sortedDevices);
      setCurrentPage(responseData?.current_page || 1);
    } catch (err) {
      console.error("Fetch Devices Error:", err);

      if (err.response?.status === 401) {
        localStorage.removeItem("token");
        navigate("/", { replace: true });
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    fetchDevices(currentPage);

    const interval = setInterval(() => {
      fetchDevices(currentPage, true);
    }, 30000);

    return () => clearInterval(interval);
  }, [currentPage]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpenMenuId(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleMenuToggle = (id, index) => {
    setDropdownPosition({
      [id]: index >= devices.length - 2 ? "dd-above" : "dd-below",
    });

    setOpenMenuId(openMenuId === id ? null : id);
  };

  const handleAction = async (type, device) => {
    try {
      setOpenMenuId(null);

      if (type === "update") {
        navigate(`/device-form/${device.id}`, {
          state: { device },
        });
        return;
      }

      if (type === "detail") {
        navigate(`/devices/${device.id}`);
        return;
      }

      if (type === "delete") {
        const confirmDelete = window.confirm(
          `Are you sure you want to delete "${device.name}"?`,
        );

        if (!confirmDelete) return;

        setActionLoadingId(device.id);

        const token = localStorage.getItem("token");

        await axios.delete(`${base}/api/devices/${device.id}`, {
          headers: token
            ? {
                Authorization: `Bearer ${token}`,
              }
            : {},
        });

        fetchDevices(currentPage, true);
      }
    } catch (err) {
      console.error("Action Error:", err);

      alert(err.response?.data?.message || "Something went wrong.");
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/", { replace: true });
  };

  return (
    <div className="dl-container">
      {loading && (
        <div className="page-loader-overlay">
          <div className="spinner"></div>
          <p>Loading devices...</p>
        </div>
      )}

      <div className="dl-topbar">
        <h2>📟 Device List</h2>

        <div className="dl-topbuttons">
          <button className="dl-logoutbtn" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </div>

      <div className="dl-tablewrapper">
        <table className="dl-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Name</th>
              <th>Location</th>
              <th>Device ID</th>
              <th>User</th>
              <th>Shape</th>
              <th>Config</th>
              <th>Sensor</th>
              <th>Full</th>
              <th>Level</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>

          <tbody>
            {devices.length > 0 ? (
              devices.map((d, index) => {
                const isOnline = d.status === "online";

                return (
                  <tr key={d.id}>
                    <td>{(currentPage - 1) * 20 + index + 1}</td>

                    <td>
                      {d.name} ({d.id})
                    </td>

                    <td>{d.location || "-"}</td>

                    <td>{d.device_id || "-"}</td>

                    <td>{d.user_name || "-"}</td>

                    <td>{d.tank_shape || "-"}</td>

                    <td>
                      {d.tank_width || d.tank_length || d.tank_radius ? (
                        <>
                          {d.tank_width && <div>W: {d.tank_width}</div>}

                          {d.tank_length && <div>L: {d.tank_length}</div>}

                          {d.tank_radius && <div>R: {d.tank_radius}</div>}
                        </>
                      ) : (
                        "-"
                      )}
                    </td>

                    <td>{d.sensor_height_bottom ?? "-"}</td>

                    <td>{d.reading_when_full ?? "-"}</td>

                    <td>
                      {d.percentage !== null && d.percentage !== undefined
                        ? `${d.percentage}%`
                        : "-"}
                    </td>

                    <td>
                      <span
                        className={
                          isOnline ? "dl-status-online" : "dl-status-offline"
                        }
                      >
                        {d.status
                          ? d.status.charAt(0).toUpperCase() + d.status.slice(1)
                          : "Offline"}
                      </span>
                    </td>

                    <td>
                      <div
                        className="dl-actionmenu"
                        ref={d.id === openMenuId ? menuRef : null}
                      >
                        <button
                          className="dl-actionbtn"
                          onClick={() => handleMenuToggle(d.id, index)}
                          disabled={actionLoadingId === d.id}
                        >
                          {actionLoadingId === d.id ? "..." : "Action"}
                        </button>

                        {openMenuId === d.id && (
                          <div
                            className={`dl-menudropdown ${
                              dropdownPosition[d.id]
                            }`}
                          >
                            <div
                              className="dl-menuitem"
                              onClick={() => handleAction("update", d)}
                            >
                              Update
                            </div>

                            <div
                              className="dl-menuitem"
                              onClick={() => handleAction("detail", d)}
                            >
                              Details
                            </div>

                            <div
                              className="dl-menuitem dl-delete"
                              onClick={() => handleAction("delete", d)}
                            >
                              Delete
                            </div>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="12">No devices found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
